import { watch, computed, defineComponent, ref, onMounted, onBeforeUnmount } from 'vue';
import { IconSize, ButtonType } from '@getaccept/fried-tofu';
import { ProsemirrorHelper, InputDocNode, ParagraphNode, MergeValueNode, EditorHelper, } from '@getaccept/editor-lib-new';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-3';
import Text from '@tiptap/extension-text';
import Focus from '@tiptap/extension-focus';
import { t } from '@getaccept/lib-shared-new/src/helpers/translation.helper';
import Placeholder from '@tiptap/extension-placeholder';
import { DateHelper } from '@getaccept/lib-shared-new/src/helpers/date.helper';
export default defineComponent({
    components: {
        EditorContent,
    },
    props: {
        hasUpdateError: { type: Boolean },
        fieldFocused: { type: Boolean },
        inputField: { type: Object, required: true },
        recipientInput: { type: Object, default: null },
        mergeValues: { type: Object, required: true },
        fields: { type: Array, default: () => [] },
        editable: { type: Boolean },
        isUpdating: { type: Boolean },
    },
    emits: ['update'],
    setup(props, { emit }) {
        const dateInputBoxRef = ref(null);
        const closeOnAction = ref(false);
        const editor = ref(null);
        const hasCleared = ref(false);
        const fieldValue = computed(() => {
            var _a;
            if ((_a = props.recipientInput) === null || _a === void 0 ? void 0 : _a.value) {
                return props.recipientInput.value;
            }
            if (props.inputField.value && !hasCleared.value) {
                return props.inputField.value;
            }
            return '';
        });
        const mergeTagValue = computed(() => ProsemirrorHelper.getMergeValueFromMergeTag(ProsemirrorHelper.getTagIdFromMergeTag(props.inputField.value), props.fields, props.mergeValues));
        const selectedMediumDate = computed(() => EditorHelper.hasMergeTags(fieldValue.value)
            ? ''
            : +fieldValue.value
                ? DateHelper.fromSecondsToMediumDate(+fieldValue.value)
                : DateHelper.fromISODateToLocaleString(fieldValue.value));
        const initialDate = computed(() => EditorHelper.hasMergeTags(fieldValue.value)
            ? mergeTagValue.value
            : +fieldValue.value
                ? DateHelper.fromSecondsToMediumDate(+fieldValue.value)
                : DateHelper.fromISODateToLocaleString(fieldValue.value));
        const initialFormattedDate = computed(() => ProsemirrorHelper.getInputContent(initialDate.value, props.fields));
        const editorConfig = computed(() => ({
            content: initialFormattedDate.value,
            editable: false,
            extensions: [
                InputDocNode,
                Focus,
                ParagraphNode,
                Text,
                MergeValueNode,
                Placeholder.configure({
                    placeholder: t('input_fields_add_date'),
                    showOnlyWhenEditable: false,
                }),
            ],
            onBlur: update,
            onUpdate: update,
            enableInputRules: false,
            enablePasteRules: false,
        }));
        const innerTextContent = computed(() => {
            var _a, _b;
            const transaction = (_a = editor.value) === null || _a === void 0 ? void 0 : _a.state.tr;
            return ProsemirrorHelper.getStringFromTransaction(transaction, (_b = editor.value) === null || _b === void 0 ? void 0 : _b.state.schema);
        });
        const update = () => {
            if (!props.editable) {
                return;
            }
            const newDate = innerTextContent.value
                ? DateHelper.fromJSDateToISODate(new Date(innerTextContent.value))
                : '';
            emit('update', newDate);
        };
        const handleDateSelect = (date) => {
            closeOnAction.value = true;
            const isoDate = DateHelper.fromJSDateToISODate(date);
            const localeString = DateHelper.fromISODateToLocaleString(isoDate);
            editor.value.commands.setContent(localeString);
            update();
        };
        const handleClear = () => {
            editor.value.commands.setContent('');
            if (!hasCleared.value) {
                hasCleared.value = true;
            }
            update();
        };
        const focus = () => {
            if (dateInputBoxRef.value) {
                dateInputBoxRef.value.$el.focus();
            }
        };
        watch(() => props.fieldFocused, (fieldFocused) => {
            if (fieldFocused) {
                focus();
            }
        });
        watch(() => [props.fields, props.mergeValues], (newValues, oldValues) => {
            if (editor.value.isDestroyed || JSON.stringify(newValues) === JSON.stringify(oldValues)) {
                return;
            }
            editor.value.commands.setContent(ProsemirrorHelper.getInputContent(initialDate.value, props.fields));
        });
        onMounted(() => {
            editor.value = new TipTapEditor(editorConfig.value);
        });
        onBeforeUnmount(() => {
            editor.value.destroy();
        });
        return {
            ButtonType,
            IconSize,
            editor,
            selectedMediumDate,
            closeOnAction,
            fieldValue,
            dateInputBoxRef,
            innerTextContent,
            hasCleared,
            handleClear,
            handleDateSelect,
            t,
            update,
        };
    },
});
